import { gql } from '@apollo/client';

export const getProposalsList = gql`
  query getProposalsList(
    $pagination: PaginationInput!
    $selectedLines: [String!]
    $selectedSublines: [String!]
    $selectedPlans: [String!]
    $selectedBusinessPartners: [String!]
    $SelectedStatuses: [String!]
    $FromEffectiveOn: DateTime
    $ToEffectiveOn: DateTime
    $selectedOwners: [String!]
    $KeywordSearch: String
    $OrderBy: OrderByInput
  ) {
    production {
      searchProposals(
        keyword: $KeywordSearch
        orderBy: $OrderBy
        pagination: $pagination
        statuses: $SelectedStatuses
        lines: $selectedLines
        sublines: $selectedSublines
        plans: $selectedPlans
        businessPartners: $selectedBusinessPartners
        effectiveOnStart: $FromEffectiveOn
        effectiveOnEnd: $ToEffectiveOn
        owners: $selectedOwners
      ) {
        paging {
          pageSize
          pageNumber
          totalCount
        }
        items {
          id
          name
          code
          policyEffectiveDate
          createdOn
          status
          statusReason
          ownerID
          mainBusinessPartner {
            code
            fullName
          }
          line {
            code
            name
          }
          subline {
            code
            name
          }
          plan {
            code
            name
          }
          owner {
            id
            proposalID
            typeList
            person {
              id
              fullName
              code
            }
            firstName
            middleName
            lastName
          }
        }
      }
    }
  }
`;

export const getProposalFilters = gql`
  query getProposalFiltersLovs($lineIDs: [String!], $sublineIDs: [String!]) {
    ProposalStatusList: __type(name: "ProposalStatusList") {
      name
      enumValues {
        name
      }
    }
    production {
      getProposalLines {
        id
        code
        name
      }
      getProposalSublines(lineIDs: $lineIDs) {
        id
        code
        name
      }
      getProposalPlans(lineIDs: $lineIDs, sublineIDs: $sublineIDs) {
        id
        code
        name
      }
      getProposalBusinessPartners {
        id
        code
        fullName
      }
      getProposalOwners {
        id
        firstName
        middleName
        lastName
      }
    }
  }
`;

export const getProposalDetails = gql`
  query getProposalDetails($id: String!) {
    production {
      getProposalDetails(id: $id) {
        name
        createdOn
        modifiedOn
        mainBusinessPartnerID
        status
        policyEffectiveDate
        policyExpiryDate
        isCorrection
        renewalFromID
        isRenewal
        renewalFrom {
          id
          policyNumber
        }
        policyType
        policyCurrency {
          code
          title
          symbol
          id
        }
        line {
          code
          name
          id
        }
        subline {
          code
          name
          id
        }
        plan {
          code
          name
          id
          insuredMaxNumber
          beneficiaryMaxNumber
          isEditableClause
          acalCategory
          sosService
          scratching
          acalCategoryEntity {
            title
          }
          replacementCarApplied
          replacementCarValue
          replacementCarDays
          grLimitDays
        }
        mainBusinessPartner {
          code
          fullName
          relatedCompany
        }
        user {
          code
          firstName
          lastName
          clauseEditRights
          groupPolicyEditRights
          planCoverEditRights
          policyEndDateEditRights
        }
      }
      getProposalPolicyMotor(proposalID: $id) {
        acalCategoryID
        acalCategory {
          id
          code
          title
        }
        scratching
        sosService
        id
        proposalID
        vehicleID
        vehicle {
          id
          plateCodeID
          make {
            id
            code
            title
          }
          modelID
          model {
            id
            code
            title
          }
          yearOfMake
          engine
          weight
          engineSize
          seatingCapacity
          chassis
          horsePower
          plateCode {
            id
            code
            title
          }
          colorID
          color {
            id
            code
            title
          }
          plateNumber
          usageTypeID
          usageType {
            id
            code
            title
          }
          bodyTypeID
          bodyType {
            id
            code
            title
          }
          engineTypeID
          engineType {
            id
            code
            title
          }
          carValue
        }
        nameOnLicenseEN
        nameOnLicenseAR
        vignetteCode
        vignette
        agencyRepairYears
        depreciationYears
      }

      getProposalPolicyMarine(proposalID: $id) {
        bank
        cargoContract
        claimsPayableBy
        createdBy
        createdOn
        departureFrom
        departureTo
        description
        id
        lcIssueDate
        lcNumber
        loading
        matterID
        modifiedBy
        modifiedOn
        policyID
        policyVersion
        policyVersionState
        proposalID
        remarks
        sumPerShipment
        sumPerShipmentCurrencyID
        sumPerShipmentPolicyCurrency
        sumPerShipmentRate
        survey
        transportationVia
        vessalAge
        vessalName
        matter {
          code
          createdOn
          id
          modifiedOn
          title
        }
        sumPerShipmentCurrency {
          code
          id
          symbol
          title
        }
      }
      getProposalPolicyPersons(proposalID: $id) {
        assignedToBusinessPartnerID
        coverLimit
        createdBy
        createdOn
        id
        linkedBusinessPartnerID
        modifiedBy
        modifiedOn
        order
        personAddressID
        personID
        address {
          addressDetails
          city
        }
        phoneNumber
        email
        person {
          code
          fullName
          passportNumber
          dateOfBirth
          type
        }
        policyID
        policyVersion
        policyVersionState
        beneficiaryPercentage
        proposalID
        isPrincipal
        relation
        cardNumber
        tpaReference1
        tpaReference2
        grApplicable
        exclusions
        remarks
        continuity
        typeList
        passportNumber
        firstName
        middleName
        lastName
        phoneNumber
        dateOfBirth
        nationalityId
        dangerousActivity
        personAddress {
          address {
            city
            addressDetails
          }
        }
      }
      getProposalPolicyPlanCovers(proposalID: $id) {
        clauseID
        clauseIsEditable
        coverID
        createdBy
        createdOn
        effectiveFrom
        effectiveTo
        excessOnClaimAmount
        excessOnClaimDays
        excessOnClaimPercentage
        excessOnClaimType
        id
        isEditable
        isMain
        isMandatory
        modifiedBy
        modifiedOn
        planCoverID
        policyID
        policyPlanCoverParentID
        policyVersion
        policyVersionState
        premiumAmount
        premiumAmountArabic
        premiumAmountArabicLetters
        premiumAmountBasic
        premiumAmountSecondary
        premiumPercentage
        premiumType
        proposalID
        sumInsured
        sumInsuredArabic
        sumInsuredArabicLetters
        sumInsuredBasic
        sumInsuredIsAdditive
        sumInsuredSecondary
        treatyID
        cover {
          abbreviation
          abbreviationArabic
          code
          createdBy
          createdOn
          description
          id
          lineID
          modifiedBy
          modifiedOn
          name
          nameArabic
          printingOrder
          treatyID
        }
        planCover {
          clauseEditable
          clauseID
          coverID
          createdBy
          createdOn
          excessOnClaimAmount
          excessOnClaimDays
          excessOnClaimPercentage
          excessOnClaimType
          id
          isEditable
          isMain
          isMandatory
          modifiedBy
          modifiedOn
          planID
          premiumAmount
          premiumPercentage
          premiumType
          sumInsured
          sumInsuredIsAdditive
        }
      }
      getProposalPolicyBankersBlanketBondsDetails(proposalID: $id) {
        branchName
        burglary
        clause6
        counterfeitCurrencies
        createdBy
        createdOn
        employeeNumber
        falsification
        forgedCheques
        holdup
        id
        infedility
        inSafe
        inTransit
        isAgency
        isMain
        modifiedBy
        modifiedOn
        policyID
        policyVersion
        policyVersionState
        proposalID
        underwritingYear
      }
      getProposalPolicyProperty(proposalID: $id) {
        order
        createdBy
        createdOn
        geoLocationID
        id
        location
        modifiedBy
        modifiedOn
        policyID
        policyVersion
        policyVersionState
        proposalID
        riskDescription
        geoLocation {
          code
          createdOn
          id
          modifiedOn
          title
        }
        planCoverPropertyDetails {
          planCoverID
          premium
          riskRate
          sumInsured
        }
      }
      getProposalPolicyRiskDescription(proposalID: $id) {
        order
        geoLocation {
          code
          createdOn
          id
          modifiedOn
          title
        }
        geoLocationID
        id
        policyID
        proposalID
        riskDescription
      }
      getProposalPolicyMarineHull(proposalID: $id) {
        builder
        connectionPort
        constructionYear
        createdBy
        createdOn
        engineFuel
        engineHorsePower
        engineMaxDesignedSpeed
        engineNumber
        engineType
        engineYearBuilt
        height
        id
        length
        modifiedBy
        modifiedOn
        policyID
        policyVersion
        policyVersionState
        proposalID
        registryPort
        remarks
        territorialWaters
        usage
        vesselName
        weight
      }
      getProposalPolicyExpat(proposalID: $id) {
        barcodeFullCode
        barcodeNumber
        continuity
        continuityNumber
        createdBy
        createdOn
        dateOfBirth
        emergency
        exclusion
        gender
        id
        medicalClassID
        modifiedBy
        modifiedOn
        nationalityID
        occupationClassID
        policyID
        policyVersion
        policyVersionState
        proposalID
        remarks
      }
      getProposalPolicyMedical(proposalID: $id) {
        medicalClassId
        levelId
        nssf
      }
      getProposalPolicyTravel(proposalID: $id) {
        destinationId
        duration
        travelDestinationId
        tripType
        departureId
        departure {
          code
          createdOn
          id
          modifiedOn
          title
        }
      }
    }
  }
`;

export const getProposalById = gql`
  query GetProposalSummary($proposalID: String!) {
    production {
      getProposalSummary(proposalID: $proposalID) {
        documents {
          documentId
          documentName
          documentPath
          documentType
        }
        summaryDetails {
          name
          lineID
          planID
          sublineID
          line
          subline
          plan
          relatedCompoany
          user
          mainBusinessPartner
          createdBy
          modifiedBy
          createdOn
          modifiedOn
          status
          statusReason
          policyId
          policyNumber
        }
        policyDetails {
          currencyID
          currency
          name
          effectiveDate
          endDate
        }
        persons {
          name
          address
          type
          limitCover
          externalCode
          mainBPID
          addressID
          beneficiaryPercentage
        }
        quote {
          policyCost
          chargesPercentage
          tpaFees
          tpaFeesType
          rate
          status
          initialDiscount
        }
        offerDetails {
          details {
            plan
            effectiveDate
            expiryDate
          }
          covers {
            name
            premium
            sumInsured
          }
          breakDown {
            basicPremium
            netPremium
            policyCost
            charges
            tpaFees
            grossPremium
            fixedStamp
            proportionalStamp
            municipalityTax
            totalPremium
            finalDiscount
          }
          coverage
          totalPremium
          maxInsured
          statusReason
          createdOn
          status
        }
      }
      getProposalPolicyPlanCovers(proposalID: $proposalID) {
        clauseID
        clauseIsEditable
        coverID
        createdBy
        createdOn
        effectiveFrom
        effectiveTo
        excessOnClaimAmount
        excessOnClaimDays
        excessOnClaimPercentage
        excessOnClaimType
        id
        isEditable
        isMain
        isMandatory
        modifiedBy
        modifiedOn
        planCoverID
        policyID
        policyPlanCoverParentID
        policyVersion
        policyVersionState
        premiumAmount
        premiumAmountArabic
        premiumAmountArabicLetters
        premiumAmountBasic
        premiumAmountSecondary
        premiumPercentage
        premiumType
        proposalID
        sumInsured
        sumInsuredArabic
        sumInsuredArabicLetters
        sumInsuredBasic
        sumInsuredIsAdditive
        sumInsuredSecondary
        planCover {
          cover {
            name
          }
          id
          coverID
          isEditable
          plan {
            planCurrencyEntity {
              code
              title
              id
              symbol
            }
          }
        }
      }
    }
  }
`;

export const getPolicyMedicalCovers = gql`
  query getPolicyMedicalCovers($planID: String!) {
    planConfig {
      getPolicyMedicalCovers(
        planID: $planID
        pagination: { pageNumber: 1, pageSize: 1000000 }
      ) {
        items {
          abbreviation
          abbreviationArabic
          code
          createdBy
          createdOn
          description
          id
          lineID
          modifiedBy
          modifiedOn
          name
          nameArabic
          printingOrder
        }
      }
    }
  }
`;

export const getPlanCoversList = gql`
  query getPlanPlanCovers($planID: String!) {
    planConfig {
      getPlanPlanCovers(
        planID: $planID
        pagination: { pageNumber: 1, pageSize: 1000000 }
      ) {
        items {
          clauseEditable
          clauseID
          coverID
          createdBy
          createdOn
          excessOnClaimAmount
          excessOnClaimDays
          excessOnClaimPercentage
          excessOnClaimType
          id
          isEditable
          isMain
          isMandatory
          modifiedBy
          modifiedOn
          planID
          premiumAmount
          premiumPercentage
          premiumType
          sumInsured
          sumInsuredIsAdditive
          cover {
            abbreviation
            abbreviationArabic
            code
            createdBy
            createdOn
            description
            id
            lineID
            modifiedBy
            modifiedOn
            name
            nameArabic
            printingOrder
          }
        }
      }
    }
  }
`;

export const getProposalMarineLovs = gql`
  query getProposalMarineLovs {
    core {
      getCurrencies {
        id
        code
        title
        symbol
      }
    }
    production {
      getMatters {
        id
        code
        title
      }
    }
  }
`;

export const getProposalExpatLovs = gql`
  query getProposalExpatLovs {
    Gender: __type(name: "Gender") {
      name
      enumValues {
        name
      }
    }
    core {
      getNationalities {
        id
        code
        title
      }
      getOccupationClass {
        id
        code
        title
      }
    }
    planConfig {
      getMedicalClasses {
        id
        code
        title
      }
    }
  }
`;

export const getBusinessPartnerPersons = gql`
  query fetchedPersons(
    $mainbusinessPartnerID: String!
    $searchKeyword: String
  ) {
    salesforce {
      getBusinessPartnerPersons(
        businessPartnerID: $mainbusinessPartnerID
        keyword: $searchKeyword
      ) {
        id
        fullName
        code
        gender
        dateOfBirth
        occupationClass
        nationalityId
        profession
        passportNumber
        type
      }
    }
  }
`;

export const fetchedPersonAddressList = gql`
  query fetchedPersonAddressList($selectedPersonID: String!) {
    salesforce {
      getPersonAddressList(
        personId: $selectedPersonID
        pagination: { pageNumber: 1, pageSize: 100000 }
      ) {
        items {
          id
          addressType
          country {
            id
            title
          }
          zone {
            id
            title
          }
          address {
            country
            city
            street
            building
          }
          isPrimary
        }
      }
    }
  }
`;

export const getProposalPropertyLovs = gql`
  query getProposalPropertyLovs($planID: String!) {
    production {
      getGeoLocations {
        id
        code
        title
      }
    }
    planConfig {
      getPlanPlanCovers(
        planID: $planID
        pagination: { pageNumber: 1, pageSize: 100000 }
      ) {
        items {
          clauseEditable
          clauseID
          coverID
          cover {
            code
            name
          }
          createdBy
          createdOn
          excessOnClaimAmount
          excessOnClaimDays
          excessOnClaimPercentage
          excessOnClaimType
          id
          isEditable
          isMain
          isMandatory
          modifiedBy
          modifiedOn
          planID
          premiumAmount
          premiumPercentage
          premiumType
          sumInsured
          sumInsuredIsAdditive
        }
      }
    }
  }
`;

export const getProposalDescriptionOfRiskLovs = gql`
  query getProposalDescriptionOfRiskLovs {
    production {
      getGeoLocations {
        id
        code
        title
      }
    }
  }
`;

export const getProposalMedicalInsuredsLovs = gql`
  query getProposalMedicalLovs {
    core {
      getNationalities {
        id
        code
        title
      }
    }
    RelationsList: __type(name: "RelationsList") {
      enumValues {
        name
      }
    }

    ContinuityList: __type(name: "ContinuityList") {
      enumValues {
        name
      }
    }
  }
`;

export const getProposalMedicalLovs = gql`
  query getProposalMedicalLovs {
    CoNSSFList: __type(name: "CoNSSFList") {
      enumValues {
        name
      }
    }
    planConfig {
      getMedicalClasses {
        id
        code
        title
      }
      getMedicalLevels {
        id
        code
        title
      }
    }
  }
`;

export const getProposalPolicyBusinessPartners = gql`
  query getProposalPolicyBusinessPartners(
    $proposalID: String!
    $orderBy: OrderByInput
    $keyword: String!
  ) {
    production {
      getProposalPolicyBusinessPartners(
        proposalID: $proposalID
        orderBy: $orderBy
        keyword: $keyword
      ) {
        id
        businessPartner {
          personId
          fullName
        }
        isPrimary
        sharePercentage
        planCommissionType
        planCommissionAmount
        planCommissionCurrencyID
        planCommissionCurrency {
          symbol
          id
        }
        planCommissionRate
        planCommissionApplicableOn {
          title
          id
        }
        commissionAmount
        policyShareCommissionAmount
        taxOnCommission
        taxOnCommissionAmount
      }
    }
  }
`;

export const checkActiveFacultativeDetailsExists = gql`
  query checkActiveFacultativeDetailsExists($policyPlanCoverId: String!) {
    reinsurance {
      checkActiveFacultativeDetailsExists(policyPlanCoverId: $policyPlanCoverId)
    }
  }
`;

export const getProposalMotorLovs = gql`
  query getProposalMotorLovs {
    core {
      getPlateCodeList {
        id
        code
        title
      }
      getUsageTypeList {
        id
        code
        title
      }
      getEngineTypeList {
        id
        code
        title
      }
      getMakeList {
        id
        code
        title
      }
      getColorList {
        id
        code
        title
      }
    }
  }
`;

export const getMotorModelLovs = gql`
  query getMotorModelLovs($makeID: String!) {
    core {
      getModelList(makeID: $makeID) {
        id
        code
        title
      }
    }
  }
`;

export const getBodyTypeList = gql`
  query getBodyTypeList($usageType: String!) {
    core {
      getBodyTypeList(usageType: $usageType) {
        id
        code
        title
      }
    }
  }
`;

export const getProposalPremiumDetails = gql`
  query getProposalPremiumDetails(
    $proposalID: String!
    $pagination: PaginationInput!
    $keyword: String
    $orderBy: OrderByInput!
  ) {
    production {
      getProposalInsuredPolicyQuoteMedicalGroupByPolicyPerson(
        proposalID: $proposalID
        pagination: $pagination
        keyword: $keyword
        orderBy: $orderBy
      ) {
        items {
          sumInsured
          age
          netPremium
          charges
          tpaFees
          grossPremium
          proportionalStamps
          municipalityTax
          totalPremium
          policyPerson {
            firstName
            middleName
            lastName
            person {
              code
              fullName
            }
            relation
          }
        }
      }
    }
  }
`;

export const getProposalTravelPremiumDetails = gql`
  query getProposalTravelPremiumDetails(
    $proposalID: String!
    $pagination: PaginationInput!
    $keyword: String
    $orderBy: OrderByInput!
  ) {
    production {
      getProposalInsuredPolicyQuoteTravelGroupByPolicyPerson(
        proposalID: $proposalID
        pagination: $pagination
        keyword: $keyword
        orderBy: $orderBy
      ) {
        items {
          sumInsured
          age
          netPremium
          charges
          tpaFees
          grossPremium
          proportionalStamps
          municipalityTax
          totalPremium
          policyPerson {
            firstName
            middleName
            lastName
            person {
              code
              fullName
            }
            relation
          }
        }
      }
    }
  }
`;

export const getProposalGroupMedicalPremiumDetails = gql`
  query getProposalGroupMedicalPremiumDetails(
    $proposalID: String!
    $pagination: PaginationInput!
    $keyword: String
    $orderBy: OrderByInput!
  ) {
    production {
      getGroupProposalInsuredPolicyQuoteMedical(
        proposalID: $proposalID
        pagination: $pagination
        keyword: $keyword
        orderBy: $orderBy
      ) {
        items {
          insured
          numberOfAdherent
          class
          level
          coNssf
          sumInsured
          netPremium
          charges
          tpaFees
          grossPremium
          proportionalStamp
          municipalityTax
          totalPremium
        }
      }
    }
  }
`;

export const downloadProposalMedicalPremiumDetails = gql`
  query downloadProposalInsuredPolicyQuoteMedical($proposalId: String!) {
    production {
      downloadProposalInsuredPolicyQuoteMedical(proposalId: $proposalId) {
        content
        contentType
        fileName
      }
    }
  }
`;

export const downloadProposalTravelPremiumDetails = gql`
  query downloadProposalInsuredPolicyQuoteTravel($proposalId: String!) {
    production {
      downloadProposalInsuredPolicyQuoteTravel(proposalId: $proposalId) {
        content
        contentType
        fileName
      }
    }
  }
`;

export const downloadProposalGroupMedicalPremiumDetails = gql`
  query downloadGroupProposalInsuredPolicyQuoteMedical($proposalId: String!) {
    production {
      downloadGroupProposalInsuredPolicyQuoteMedical(proposalId: $proposalId) {
        content
        contentType
        fileName
      }
    }
  }
`;

export const getTravelLovs = gql`
  query getTravelLovs($planID: String!) {
    planConfig {
      getPolicyTravelCovers(
        planID: $planID
        pagination: { pageNumber: 1, pageSize: 1000000 }
      ) {
        items {
          abbreviation
          abbreviationArabic
          code
          createdBy
          createdOn
          description
          id
          lineID
          modifiedBy
          modifiedOn
          name
          nameArabic
          printingOrder
        }
      }
      getTravelDestinationsLookup {
        id
        code
        title
      }
    }
    __type(name: "TripTypeList") {
      name
      enumValues {
        name
      }
    }
  }
`;

export const getActiveFacultativeDetailsByPolicyPlanCover = gql`
  query getActiveFacultativeDetailsByPolicyPlanCover(
    $policyPlanCoverID: String!
  ) {
    reinsurance {
      getActiveFacultativeDetailsByPolicyPlanCover(
        policyPlanCoverID: $policyPlanCoverID
      ) {
        cededSumInsured
        cededPremium
        policyPlanCoverID
      }
    }
  }
`;

export const getProposalPlans = gql`
  query getProposalPlans($lineIDs: [String!], $sublineIDs: [String!]) {
    production {
      getProposalPlans(
        lineIDs: $lineIDs
        sublineIDs: $sublineIDs
        status: "ACTIVE"
      ) {
        name
        id
        code
        planCurrency
      }
    }
  }
`;

export const getPlans = gql`
  query getPlans($lineIDs: [String!], $sublineIDs: [String!]) {
    planConfig {
      getPlans(lines: $lineIDs, sublines: $sublineIDs, statuses: ["Active"]) {
        items {
          name
          id
          code
          planCurrency
        }
      }
    }
  }
`;

export const getExportProposalsLOVs = gql`
  query GetExportProposalsLOVs {
    ProposalStatusList: __type(name: "ProposalStatusList") {
      name
      enumValues {
        name
      }
    }
    production {
      getProposalBusinessPartners {
        id
        code
        fullName
      }
    }
    core {
      getCurrencies {
        id
        code
        title
      }
    }
  }
`;

export const searchUsersForExport = gql`
  query SearchUsersForExport(
    $keyword: String
    $pageNumber: Int!
    $pageSize: Int!
  ) {
    core {
      searchUsers(
        pagination: { pageNumber: $pageNumber, pageSize: $pageSize }
        keyword: $keyword
      ) {
        items {
          id
          code
          firstName
          middleName
          lastName
        }
      }
    }
  }
`;

export const searchProposalsForExport = gql`
  query searchProposalsForExport(
    $currentPage: Int!
    $currentPageSize: Int!
    $selectedBusinessPartners: [String!]
    $effectiveOnStart: DateTime
    $effectiveOnEnd: DateTime
    $expiryOnStart: DateTime
    $expiryOnEnd: DateTime
    $issueOnStart: DateTime
    $issueOnEnd: DateTime
    $createdOnStart: DateTime
    $createdOnEnd: DateTime
    $Attribute: String!
    $Descending: Boolean!
  ) {
    production {
      searchProposals(
        pagination: { pageNumber: $currentPage, pageSize: $currentPageSize }
        orderBy: { attribute: $Attribute, descending: $Descending }
        businessPartners: $selectedBusinessPartners
        effectiveOnStart: $effectiveOnStart
        effectiveOnEnd: $effectiveOnEnd
        expiryOnStart: $expiryOnStart
        expiryOnEnd: $expiryOnEnd
        issueOnStart: $issueOnStart
        issueOnEnd: $issueOnEnd
        createdOnStart: $createdOnStart
        createdOnEnd: $createdOnEnd
      ) {
        paging {
          pageNumber
          pageSize
          totalCount
        }
        items {
          name
          plan {
            code
            name
          }
          mainBusinessPartner {
            code
            fullName
          }
          user {
            id
            code
            firstName
            lastName
          }
          policyEffectiveDate
          policyExpiryDate
          policyIssueDate
          policyCurrency {
            id
            title
          }
          isRenewal
          renewalFromID
          renewalFrom {
            name
            policyNumber
          }
          status
          statusReason
        }
      }
    }
  }
`;
